import { PackDefinition, PackKind, PackVariant } from "types/packs/pack-definition";
import { PokeNames } from "types/pokemon/poke-names";
import { PokeType } from "types/pokemon/poke-type";
import { capitalizeFirstLetter } from "utils/string-utils";
import { PackTier } from "utils/pokemon/pack-tier-classifier";

/**
 * core-packs (C2) static pack content.
 *
 * NORMAL pack shells (which type(s) + tier a pack covers) were decided from a real
 * population report (`scripts/packs/pack-coverage-report.ts --mode=population`):
 * types with very few eligible species at a given tier are merged into a shared shell,
 * types with a healthy population stand alone. The shells fully partition all 18 types
 * per tier (no type is left out, none is covered twice within the same tier).
 *
 * Each shell's `pool` is HAND-CURATED, not mechanically generated. This first pass
 * assigns every currently-eligible species to the shell matching its tier and PRIMARY
 * type (unambiguous, since shells partition types per tier). Pools are expected to keep
 * evolving as an ongoing task -- use `scripts/packs/pack-coverage-report.ts
 * --mode=uncovered` to find species not yet placed anywhere (e.g. newly sprite-enabled
 * species) and refine/reshuffle placements for thematic fit over time.
 *
 * ENERGY_ONLY packs need no curation (fixed single type, flat grant amount).
 */

interface PackShell {
  key: string;
  name: string;
  tier: PackTier;
  types: PokeType[];
  /** Hand-curated base-stage species pool -- see file header. */
  pool: PokeNames[];
}

const SHELLS: PackShell[] = [
  // --- JUNK ---
  {
    key: "junk_fringe_oddities",
    name: "Fringe Oddities (Common)",
    tier: PackTier.JUNK,
    types: [PokeType.GHOST, PokeType.DRAGON, PokeType.FIRE, PokeType.FIGHTING, PokeType.STEEL],
    pool: [
      PokeNames.BRONZOR,
      PokeNames.CRABRAWLER,
      PokeNames.LITLEO,
      PokeNames.NUMEL,
      PokeNames.PANCHAM,
      PokeNames.SHUPPET,
    ],
  },
  {
    key: "junk_frozen_rubble",
    name: "Frozen Rubble (Common)",
    tier: PackTier.JUNK,
    types: [PokeType.ICE, PokeType.ROCK],
    pool: [
      PokeNames.BONSLY,
      PokeNames.GEODUDE,
      PokeNames.NACLI,
      PokeNames.NOSEPASS,
      PokeNames.ROGGENROLA,
      PokeNames.SPHEAL,
      PokeNames.SWINUB,
      PokeNames.VANILLITE,
    ],
  },
  {
    key: "junk_twilight_charm",
    name: "Twilight Charm (Common)",
    tier: PackTier.JUNK,
    types: [PokeType.DARK, PokeType.FAIRY],
    pool: [
      PokeNames.FLABEBE,
      PokeNames.IMPIDIMP,
      PokeNames.MILCERY,
      PokeNames.NICKIT,
      PokeNames.POOCHYENA,
      PokeNames.PURRLOIN,
      PokeNames.SPRITZEE,
      PokeNames.SWIRLIX,
    ],
  },
  {
    key: "junk_grounded_circuit",
    name: "Grounded Circuit (Common)",
    tier: PackTier.JUNK,
    types: [PokeType.ELECTRIC, PokeType.GROUND],
    pool: [
      PokeNames.BALTOY,
      PokeNames.DIGLETT,
      PokeNames.EMOLGA,
      PokeNames.MAREEP,
      PokeNames.MINUN,
      PokeNames.PACHIRISU,
      PokeNames.PLUSLE,
      PokeNames.SANDSHREW,
      PokeNames.SHINX,
      PokeNames.SILICOBRA,
      PokeNames.TRAPINCH,
      PokeNames.YAMPER,
    ],
  },
  {
    key: "junk_psychic",
    name: "Mind Spark (Common)",
    tier: PackTier.JUNK,
    types: [PokeType.PSYCHIC],
    pool: [
      PokeNames.ABRA,
      PokeNames.ELGYEM,
      PokeNames.GOTHITA,
      PokeNames.HATENNA,
      PokeNames.RALTS,
      PokeNames.SOLOSIS,
      PokeNames.SPOINK,
      PokeNames.UNOWN,
    ],
  },
  {
    key: "junk_poison_flying",
    name: "Toxic Skies (Common)",
    tier: PackTier.JUNK,
    types: [PokeType.POISON, PokeType.FLYING],
    pool: [
      PokeNames.EKANS,
      PokeNames.GULPIN,
      PokeNames.SKRELP,
      PokeNames.STUNKY,
      PokeNames.ZUBAT,
      PokeNames.ROOKIDEE,
    ],
  },
  {
    key: "junk_bug",
    name: "Creepy Crawlers (Common)",
    tier: PackTier.JUNK,
    types: [PokeType.BUG],
    pool: [
      PokeNames.BLIPBUG,
      PokeNames.CATERPIE,
      PokeNames.GRUBBIN,
      PokeNames.KRICKETOT,
      PokeNames.LEDYBA,
      PokeNames.NINCADA,
      PokeNames.SCATTERBUG,
      PokeNames.SEWADDLE,
      PokeNames.SPINARAK,
      PokeNames.SURSKIT,
      PokeNames.VENIPEDE,
      PokeNames.WEEDLE,
      PokeNames.WURMPLE,
    ],
  },
  {
    key: "junk_grass",
    name: "Sprout Patch (Common)",
    tier: PackTier.JUNK,
    types: [PokeType.GRASS],
    pool: [
      PokeNames.APPLIN,
      PokeNames.BELLSPROUT,
      PokeNames.BOUNSWEET,
      PokeNames.BUDEW,
      PokeNames.CARNIVINE,
      PokeNames.FERROSEED,
      PokeNames.HOPPIP,
      PokeNames.MARACTUS,
      PokeNames.ODDISH,
      PokeNames.SEEDOT,
      PokeNames.SHROOMISH,
      PokeNames.SKIDDO,
      PokeNames.SMOLIV,
      PokeNames.SUNKERN,
      PokeNames.TROPIUS,
    ],
  },
  {
    key: "junk_water",
    name: "Tide Pool (Common)",
    tier: PackTier.JUNK,
    types: [PokeType.WATER],
    pool: [
      PokeNames.ARROKUDA,
      PokeNames.CARVANHA,
      PokeNames.CHEWTLE,
      PokeNames.CLAMPERL,
      PokeNames.CLAUNCHER,
      PokeNames.CORPHISH,
      PokeNames.DEWPIDER,
      PokeNames.FEEBAS,
      PokeNames.FINIZEN,
      PokeNames.GOLDEEN,
      PokeNames.HORSEA,
      PokeNames.KRABBY,
      PokeNames.LOTAD,
      PokeNames.LUVDISC,
      PokeNames.MAGIKARP,
      PokeNames.POLIWAG,
      PokeNames.STARYU,
      PokeNames.TYMPOLE,
      PokeNames.WIGLETT,
      PokeNames.WOOPER,
    ],
  },
  {
    key: "junk_normal",
    name: "Everyday Critters (Common)",
    tier: PackTier.JUNK,
    types: [PokeType.NORMAL],
    pool: [
      PokeNames.AUDINO,
      PokeNames.BIDOOF,
      PokeNames.BUNNELBY,
      PokeNames.FLETCHLING,
      PokeNames.HOOTHOOT,
      PokeNames.KECLEON,
      PokeNames.LECHONK,
      PokeNames.LILLIPUP,
      PokeNames.MEOWTH,
      PokeNames.MINCCINO,
      PokeNames.PATRAT,
      PokeNames.PIDGEY,
      PokeNames.PIDOVE,
      PokeNames.RATTATA,
      PokeNames.SENTRET,
      PokeNames.SKITTY,
      PokeNames.SLAKOTH,
      PokeNames.SPEAROW,
      PokeNames.SPINDA,
      PokeNames.STARLY,
      PokeNames.SWABLU,
      PokeNames.TAILLOW,
      PokeNames.WOOLOO,
      PokeNames.ZIGZAGOON,
    ],
  },

  // --- STANDARD ---
  {
    key: "standard_dragon_steel_dark",
    name: "Iron Shadow (Standard)",
    tier: PackTier.STANDARD,
    types: [PokeType.DRAGON, PokeType.STEEL, PokeType.DARK],
    pool: [
      PokeNames.ARON,
      PokeNames.AXEW,
      PokeNames.HOUNDOUR,
      PokeNames.INKAY,
      PokeNames.MURKROW,
      PokeNames.SCRAGGY,
      PokeNames.SNEASEL,
      PokeNames.VAROOM,
      PokeNames.VULLABY,
      PokeNames.ZORUA,
    ],
  },
  {
    key: "standard_ice",
    name: "Frostbite (Standard)",
    tier: PackTier.STANDARD,
    types: [PokeType.ICE],
    pool: [
      PokeNames.BERGMITE,
      PokeNames.CETODDLE,
      PokeNames.CUBCHOO,
      PokeNames.SMOOCHUM,
      PokeNames.SNOM,
      PokeNames.SNORUNT,
    ],
  },
  {
    key: "standard_fairy",
    name: "Moonlit Charm (Standard)",
    tier: PackTier.STANDARD,
    types: [PokeType.FAIRY],
    pool: [PokeNames.CLEFFA, PokeNames.FIDOUGH, PokeNames.SNUBBULL, PokeNames.TOGEPI],
  },
  {
    key: "standard_fighting_flying",
    name: "Sky Fist (Standard)",
    tier: PackTier.STANDARD,
    types: [PokeType.FIGHTING, PokeType.FLYING],
    pool: [
      PokeNames.CLOBBOPUS,
      PokeNames.MACHOP,
      PokeNames.MAKUHITA,
      PokeNames.MEDITITE,
      PokeNames.MIENFOO,
      PokeNames.RIOLU,
      PokeNames.TIMBURR,
      PokeNames.TYROGUE,
      PokeNames.NOIBAT,
    ],
  },
  {
    key: "standard_ghost",
    name: "Haunted Hollow (Standard)",
    tier: PackTier.STANDARD,
    types: [PokeType.GHOST],
    pool: [
      PokeNames.DRIFLOON,
      PokeNames.DUSKULL,
      PokeNames.GASTLY,
      PokeNames.GREAVARD,
      PokeNames.LITWICK,
      PokeNames.MISDREAVUS,
      PokeNames.PHANTUMP,
      PokeNames.SANDYGAST,
      PokeNames.SINISTEA,
      PokeNames.YAMASK,
    ],
  },
  {
    key: "standard_electric",
    name: "Live Wire (Standard)",
    tier: PackTier.STANDARD,
    types: [PokeType.ELECTRIC],
    pool: [
      PokeNames.ELECTRIKE,
      PokeNames.ELEKID,
      PokeNames.HELIOPTILE,
      PokeNames.MAGNEMITE,
      PokeNames.PAWMI,
      PokeNames.PICHU,
      PokeNames.TADBULB,
      PokeNames.TOXEL,
      PokeNames.TYNAMO,
      PokeNames.VOLTORB,
      PokeNames.WATTREL,
    ],
  },
  {
    key: "standard_ground",
    name: "Dust Trail (Standard)",
    tier: PackTier.STANDARD,
    types: [PokeType.GROUND],
    pool: [
      PokeNames.CUBONE,
      PokeNames.DRILBUR,
      PokeNames.GLIGAR,
      PokeNames.GOLETT,
      PokeNames.HIPPOPOTAS,
      PokeNames.MUDBRAY,
      PokeNames.PHANPY,
      PokeNames.RHYHORN,
      PokeNames.SANDILE,
      PokeNames.TOEDSCOOL,
    ],
  },
  {
    key: "standard_psychic",
    name: "Third Eye (Standard)",
    tier: PackTier.STANDARD,
    types: [PokeType.PSYCHIC],
    pool: [
      PokeNames.CHINGLING,
      PokeNames.DROWZEE,
      PokeNames.ESPURR,
      PokeNames.FLITTLE,
      PokeNames.MUNNA,
      PokeNames.NATU,
      PokeNames.WOOBAT,
      PokeNames.WYNAUT,
    ],
  },
  {
    key: "standard_bug",
    name: "Swarm Call (Standard)",
    tier: PackTier.STANDARD,
    types: [PokeType.BUG],
    pool: [
      PokeNames.BURMY,
      PokeNames.COMBEE,
      PokeNames.CUTIEFLY,
      PokeNames.DWEBBLE,
      PokeNames.JOLTIK,
      PokeNames.NYMBLE,
      PokeNames.PARAS,
      PokeNames.PINECO,
      PokeNames.VENONAT,
      PokeNames.WIMPOD,
      PokeNames.YANMA,
    ],
  },
  {
    key: "standard_poison",
    name: "Venom Drip (Standard)",
    tier: PackTier.STANDARD,
    types: [PokeType.POISON],
    pool: [
      PokeNames.CROAGUNK,
      PokeNames.GRIMER,
      PokeNames.KOFFING,
      PokeNames.MAREANIE,
      PokeNames.SALANDIT,
      PokeNames.SKORUPI,
      PokeNames.TRUBBISH,
    ],
  },

  {
    key: "standard_rock",
    name: "Fossil Vault (Standard)",
    tier: PackTier.STANDARD,
    types: [PokeType.ROCK],
    pool: [
      PokeNames.AMAURA,
      PokeNames.ANORITH,
      PokeNames.ARCHEN,
      PokeNames.BINACLE,
      PokeNames.CRANIDOS,
      PokeNames.GLIMMET,
      PokeNames.KABUTO,
      PokeNames.LILEEP,
      PokeNames.OMANYTE,
      PokeNames.ONIX,
      PokeNames.ROCKRUFF,
      PokeNames.SHIELDON,
      PokeNames.TYRUNT,
    ],
  },
  {
    key: "standard_normal",
    name: "Wild Bunch (Standard)",
    tier: PackTier.STANDARD,
    types: [PokeType.NORMAL],
    pool: [
      PokeNames.AIPOM,
      PokeNames.AZURILL,
      PokeNames.BUNEARY,
      PokeNames.DEERLING,
      PokeNames.DODUO,
      PokeNames.EEVEE,
      PokeNames.GLAMEOW,
      PokeNames.IGGLYBUFF,
      PokeNames.LICKITUNG,
      PokeNames.MUNCHLAX,
      PokeNames.PORYGON,
      PokeNames.RUFFLET,
      PokeNames.SMEARGLE,
      PokeNames.STUFFUL,
      PokeNames.TANDEMAUS,
      PokeNames.WHISMUR,
    ],
  },
  {
    key: "standard_fire",
    name: "Ember Coals (Standard)",
    tier: PackTier.STANDARD,
    types: [PokeType.FIRE],
    pool: [
      PokeNames.CHARMANDER,
      PokeNames.CHIMCHAR,
      PokeNames.CYNDAQUIL,
      PokeNames.DARUMAKA,
      PokeNames.FENNEKIN,
      PokeNames.GROWLITHE,
      PokeNames.LITTEN,
      PokeNames.MAGBY,
      PokeNames.PANSEAR,
      PokeNames.PONYTA,
      PokeNames.SIZZLIPEDE,
      PokeNames.SLUGMA,
      PokeNames.TEPIG,
      PokeNames.TORCHIC,
      PokeNames.VULPIX,
    ],
  },
  {
    key: "standard_grass",
    name: "Budding Grove (Standard)",
    tier: PackTier.STANDARD,
    types: [PokeType.GRASS],
    pool: [
      PokeNames.BULBASAUR,
      PokeNames.CACNEA,
      PokeNames.CAPSAKID,
      PokeNames.CHERUBI,
      PokeNames.CHESPIN,
      PokeNames.CHIKORITA,
      PokeNames.COTTONEE,
      PokeNames.EXEGGCUTE,
      PokeNames.FOMANTIS,
      PokeNames.FOONGUS,
      PokeNames.GOSSIFLEUR,
      PokeNames.MORELULL,
      PokeNames.PANSAGE,
      PokeNames.PETILIL,
      PokeNames.ROWLET,
      PokeNames.SNIVY,
      PokeNames.SNOVER,
      PokeNames.TANGELA,
      PokeNames.TREECKO,
      PokeNames.TURTWIG,
    ],
  },
  {
    key: "standard_water",
    name: "Riverbend (Standard)",
    tier: PackTier.STANDARD,
    types: [PokeType.WATER],
    pool: [
      PokeNames.BARBOACH,
      PokeNames.BUIZEL,
      PokeNames.CHINCHOU,
      PokeNames.FINNEON,
      PokeNames.FROAKIE,
      PokeNames.MANTYKE,
      PokeNames.MUDKIP,
      PokeNames.OSHAWOTT,
      PokeNames.PANPOUR,
      PokeNames.PIPLUP,
      PokeNames.POPPLIO,
      PokeNames.PSYDUCK,
      PokeNames.QWILFISH,
      PokeNames.REMORAID,
      PokeNames.SEEL,
      PokeNames.SHELLDER,
      PokeNames.SHELLOS,
      PokeNames.SLOWPOKE,
      PokeNames.SQUIRTLE,
      PokeNames.TENTACOOL,
      PokeNames.TOTODILE,
      PokeNames.WAILMER,
      PokeNames.WINGULL,
    ],
  },

  // --- STRONG ---
  {
    key: "strong_wild_types",
    name: "Untamed Frontier (Advanced)",
    tier: PackTier.STRONG,
    types: [PokeType.GRASS, PokeType.POISON, PokeType.GROUND, PokeType.FIRE, PokeType.FIGHTING],
    pool: [
      PokeNames.FUECOCO,
      PokeNames.GROOKEY,
      PokeNames.HAWLUCHA,
      PokeNames.HEATMOR,
      PokeNames.MANKEY,
      PokeNames.PASSIMIAN,
      PokeNames.SCORBUNNY,
      PokeNames.SEVIPER,
      PokeNames.SPRIGATITO,
      PokeNames.STUNFISK,
      PokeNames.TORKOAL,
      PokeNames.TURTONATOR,
    ],
  },
  {
    key: "strong_shadow_frost",
    name: "Shadow Frost (Advanced)",
    tier: PackTier.STRONG,
    types: [PokeType.DARK, PokeType.GHOST, PokeType.ICE],
    pool: [
      PokeNames.ABSOL,
      PokeNames.CRYOGONAL,
      PokeNames.DELIBIRD,
      PokeNames.DHELMISE,
      PokeNames.SABLEYE,
      PokeNames.SPIRITOMB,
    ],
  },
  {
    key: "strong_mystic_dragon_steel",
    name: "Arcane Vanguard (Advanced)",
    tier: PackTier.STRONG,
    types: [PokeType.PSYCHIC, PokeType.FAIRY, PokeType.DRAGON, PokeType.STEEL],
    pool: [
      PokeNames.COMFEY,
      PokeNames.CYCLIZAR,
      PokeNames.DRUDDIGON,
      PokeNames.KLEFKI,
      PokeNames.MAWILE,
      PokeNames.ORTHWORM,
      PokeNames.SIGILYPH,
      PokeNames.SKARMORY,
      PokeNames.TINKATINK,
    ],
  },
  {
    key: "strong_static_swarm",
    name: "Static Swarm (Advanced)",
    tier: PackTier.STRONG,
    types: [PokeType.ELECTRIC, PokeType.BUG],
    pool: [
      PokeNames.ARCTOZOLT,
      PokeNames.DEDENNE,
      PokeNames.DRACOZOLT,
      PokeNames.DURANT,
      PokeNames.HERACROSS,
      PokeNames.ILLUMISE,
      PokeNames.PINCURCHIN,
      PokeNames.PINSIR,
      PokeNames.ROTOM,
      PokeNames.SCYTHER,
      PokeNames.SHUCKLE,
      PokeNames.TOGEDEMARU,
      PokeNames.VOLBEAT,
    ],
  },
  {
    key: "strong_rock_flying",
    name: "Skyfall Ridge (Advanced)",
    tier: PackTier.STRONG,
    types: [PokeType.ROCK, PokeType.FLYING],
    pool: [
      PokeNames.CRAMORANT,
      PokeNames.AERODACTYL,
      PokeNames.CARBINK,
      PokeNames.LUNATONE,
      PokeNames.SOLROCK,
      PokeNames.STONJOURNER,
    ],
  },
  {
    key: "strong_water",
    name: "Deep Current (Advanced)",
    tier: PackTier.STRONG,
    types: [PokeType.WATER],
    pool: [
      PokeNames.ARCTOVISH,
      PokeNames.BRUXISH,
      PokeNames.CORSOLA,
      PokeNames.DRACOVISH,
      PokeNames.LAPRAS,
      PokeNames.PYUKUMUKU,
      PokeNames.QUAXLY,
      PokeNames.RELICANTH,
      PokeNames.SOBBLE,
      PokeNames.VELUZA,
    ],
  },
  {
    key: "strong_normal",
    name: "Alpha Pack (Advanced)",
    tier: PackTier.STRONG,
    types: [PokeType.NORMAL],
    pool: [
      PokeNames.CASTFORM,
      PokeNames.CHATOT,
      PokeNames.DITTO,
      PokeNames.DRAMPA,
      PokeNames.DUNSPARCE,
      PokeNames.FURFROU,
      PokeNames.GIRAFARIG,
      PokeNames.HAPPINY,
      PokeNames.KANGASKHAN,
      PokeNames.MILTANK,
      PokeNames.STANTLER,
      PokeNames.TAUROS,
      PokeNames.TEDDIURSA,
      PokeNames.ZANGOOSE,
    ],
  },

  // --- PREMIUM ---
  {
    key: "premium_rare_breeds",
    name: "Rare Magic (Elite)",
    tier: PackTier.PREMIUM,
    types: [PokeType.BUG, PokeType.DARK, PokeType.DRAGON, PokeType.FIRE, PokeType.GROUND, PokeType.ROCK, PokeType.STEEL],
    pool: [
      PokeNames.CHARCADET,
      PokeNames.DEINO,
      PokeNames.LARVESTA,
      PokeNames.LARVITAR,
      PokeNames.PAWNIARD,
    ],
  },
  {
    key: "premium_iron_wyrm",
    name: "Iron Wyrm (Elite)",
    tier: PackTier.PREMIUM,
    types: [PokeType.STEEL, PokeType.DRAGON],
    pool: [
      PokeNames.BAGON,
      PokeNames.BELDUM,
      PokeNames.DRATINI,
      PokeNames.DREEPY,
      PokeNames.DURALUDON,
      PokeNames.FRIGIBAX,
      PokeNames.GIBLE,
      PokeNames.GOOMY,
      PokeNames.HONEDGE,
    ],
  },
];

/** Playtest-tunable: how much stronger the shiny roll is on a SHINY_BOOST variant. */
const SHINY_BOOST_MULTIPLIER = 2;
/** Playtest-tunable: talent-purity roll bias added on a TALENT_BOOST variant. */
const TALENT_BOOST_PURITY_BIAS = 2;

const buildVariants = (shell: PackShell): PackDefinition[] => [
  {
    key: `${shell.key}_base`,
    name: shell.name,
    kind: PackKind.NORMAL,
    types: shell.types,
    tier: shell.tier,
    variant: PackVariant.BASE,
    pool: shell.pool,
    shiny_rate_multiplier: 1,
  },
  {
    key: `${shell.key}_shiny_boost`,
    name: `${shell.name} (Shiny Boost)`,
    kind: PackKind.NORMAL,
    types: shell.types,
    tier: shell.tier,
    variant: PackVariant.SHINY_BOOST,
    pool: shell.pool,
    shiny_rate_multiplier: SHINY_BOOST_MULTIPLIER,
  },
  {
    key: `${shell.key}_talent_boost`,
    name: `${shell.name} (Talent Boost)`,
    kind: PackKind.NORMAL,
    types: shell.types,
    tier: shell.tier,
    variant: PackVariant.TALENT_BOOST,
    pool: shell.pool,
    talent_purity_bias: TALENT_BOOST_PURITY_BIAS,
  },
];

const NORMAL_PACKS: PackDefinition[] = SHELLS.flatMap(buildVariants);

/** Playtest-tunable: must exceed the 2 energy a NORMAL pack already grants for free. */
const ENERGY_ONLY_AMOUNT = 6;

const ENERGY_ONLY_PACKS: PackDefinition[] = Object.values(PokeType).map((type) => ({
  key: `energy_${type}`,
  name: `${capitalizeFirstLetter(type)} Energy Core`,
  kind: PackKind.ENERGY_ONLY,
  types: [type],
  energy_amount: ENERGY_ONLY_AMOUNT,
}));

export const PACK_DEFINITIONS: PackDefinition[] = [...NORMAL_PACKS, ...ENERGY_ONLY_PACKS];
